import { globalObservability } from '@opendoor/observability/slim';
import { z } from 'zod';

import type { SellerAddressFlow } from '../../helpers/addressValidation';
import {
  addressValidationResultSchema,
  handleAddressVerificationSuccess,
} from '../../helpers/AddressValidationContext';
import {
  clearManualAddressDraft,
  rememberManualAddressHandoff,
} from '../../helpers/manualAddressDraft';
import { fetchCustomerAction, getCustomerNavigationHref } from '../account/impersonation';
import { setCookie } from '../shared/trackers';
import { redactGraphqlRequestErrorForTelemetry } from './graphqlRequestError';

const standardizedAddressSchema = z.object({
  id: z.string().min(1),
  street1: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1),
  postal_code: z.string().min(1),
  unit: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  result: z.array(addressValidationResultSchema).default([]),
  standardize_unavailable: z.literal(false).optional(),
});
const unitsSchema = z.object({ hasMultipleUnits: z.boolean() });
const sellerInputSchema = z.object({
  error: z.boolean().optional(),
  uuid: z.uuid().optional(),
  pdpUrl: z.string().optional(),
});
const PROPERTY_PAGE_PATH = /^\/properties\/[A-Za-z0-9][A-Za-z0-9._~-]*\/aid_[0-9a-fA-F-]{36}$/;

export const homepageSellerAddressFlow: SellerAddressFlow = {
  async standardizeAddress(address) {
    const response = await fetch('/_address-input/standardize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        street1: address.street1,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        ...(address.unit ? { unit: address.unit } : {}),
      }),
      signal: AbortSignal.timeout(10000),
    });
    if (!response.ok) throw new Error('Address standardization unavailable');
    const addressResult = standardizedAddressSchema.parse(await response.json());
    return {
      id: addressResult.id,
      street: addressResult.street1,
      city: addressResult.city,
      state: addressResult.state,
      postalCode: addressResult.postal_code,
      unitNumber: addressResult.unit ?? null,
      latitude: addressResult.latitude ?? null,
      longitude: addressResult.longitude ?? null,
      validationResults: addressResult.result,
    };
  },
  async requiresUnitSelection(addressId) {
    try {
      const response = await fetch(
        `/_address-input/list-units?addressUuid=${encodeURIComponent(addressId)}`,
        {
          signal: AbortSignal.timeout(10000),
        },
      );
      if (!response.ok) throw new Error('Address unit lookup unavailable');
      return unitsSchema.parse(await response.json()).hasMultipleUnits;
    } catch (error) {
      globalObservability
        .getSentryClient()
        .captureException?.(redactGraphqlRequestErrorForTelemetry(error));
      // The homepage defers the unit check to the seller flow if this optional read fails.
      return false;
    }
  },
  async proceed({
    address,
    relationship,
    channel,
    partnerReferralId,
    trackingKeywords,
    trackingTaxonomy,
    trackEvent,
    onSuccess,
    pageSetPath,
    originSurface,
    signal,
    userConfirmed,
    canRedirect = true,
  }) {
    signal?.throwIfAborted();
    if (trackingTaxonomy) setCookie('sourceForTracking', trackingTaxonomy, 1);
    const searchSessionId = new URLSearchParams(window.location.search).get('searchSessionId');
    const response = await fetchCustomerAction('/_address-input/create-seller-input', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        address: {
          ...(address.id ? { uuid: address.id } : {}),
          street1: address.street1,
          unit: address.unit ?? '',
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
          ...(userConfirmed ? { user_confirmed: true } : {}),
        },
        ...(channel ? { channel } : {}),
        ...(relationship
          ? { relationship: relationship === 'other' ? 'Other' : relationship }
          : {}),
        ...(trackingKeywords ? { trackingKeywords } : {}),
        ...(partnerReferralId ? { partnerReferralId } : {}),
        ...(searchSessionId ? { searchSessionId } : {}),
        analyticsMetadata: { product: 'SELL_DIRECT', url: window.location.href },
      }),
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(10000)])
        : AbortSignal.timeout(10000),
    });
    signal?.throwIfAborted();
    if (response.status === 400) {
      if (!canRedirect) throw new Error('Seller address was rejected');
      rememberManualAddressHandoff({
        searchSessionId: searchSessionId ?? undefined,
        fields: {
          street: address.street1,
          unit: address.unit ?? '',
          city: address.city,
          state: address.state,
          postalCode: address.postal_code,
        },
      });
      const query = new URLSearchParams({
        ...(originSurface ? { originSurface } : {}),
        ...(channel ? { channel } : {}),
        ...(trackingKeywords ? { trackingKeywords } : {}),
        ...(partnerReferralId ? { partnerReferralId } : {}),
        ...(pageSetPath ? { pageSetPath } : {}),
        ...(searchSessionId ? { searchSessionId } : {}),
      });
      window.location.href = getCustomerNavigationHref(`/manual-address-entry?${query}`);
      return;
    }
    const result = sellerInputSchema.parse(await response.json());
    if (!response.ok || result.error || !result.uuid)
      throw new Error('Seller address handoff failed');
    signal?.throwIfAborted();
    const pdpPath =
      result.pdpUrl && !result.pdpUrl.includes('..') && PROPERTY_PAGE_PATH.test(result.pdpUrl)
        ? result.pdpUrl
        : undefined;
    await handleAddressVerificationSuccess({
      address,
      token: result.uuid,
      addressConfirmed: Boolean(relationship),
      questionnaireRouting: 'homepage',
      relationship,
      pdpUrl: pdpPath,
      trackEvent,
      onSuccess,
      pageSetPath,
      originSurface,
      signal,
    });
    if (!signal?.aborted) clearManualAddressDraft();
  },
};
